import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, Eye, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import {
  DEFAULT_SCHEDULE_RULES,
  SCHEDULE_PRESETS,
  scheduleRulesSchema,
  type Platform,
  type ScheduleRules,
  type TimeWindow,
} from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands } from "@/lib/queries";
import {
  useBrandSchedule,
  useClearOverride,
  usePreviewSchedule,
  useSaveSchedule,
  type PreviewSlot,
} from "@/lib/schedule";
import { ErrorBox } from "./LoginPage";

/**
 * Schedule settings (spec §6, §8).
 *
 * Leads with presets and three plain questions — which days, between which
 * times, roughly how many a week — because the full rule set has nine fields
 * and most people have no opinion about jitter. Advanced holds the rest.
 *
 * The preview is the point of the screen: "about 4 posts a week in the evening"
 * is impossible to sanity-check without seeing the actual minutes it produces.
 */

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

export function SchedulePage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId && brands.data?.length) setBrandId(brands.data[0]!.id);
  }, [brands.data, brandId]);

  const schedule = useBrandSchedule(brandId);
  const save = useSaveSchedule(brandId);
  const clearOverride = useClearOverride(brandId);
  const preview = usePreviewSchedule(brandId);

  /** null = editing the brand default; an id = editing that page's override. */
  const [target, setTarget] = useState<string | null>(null);
  const [rules, setRules] = useState<ScheduleRules>(DEFAULT_SCHEDULE_RULES);
  const [advanced, setAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [slots, setSlots] = useState<PreviewSlot[] | null>(null);

  const data = schedule.data;

  // Load whichever rule set the user switched to.
  useEffect(() => {
    if (!data) return;
    if (target === null) {
      setRules(data.brandRules ?? data.defaultRules);
    } else {
      const page = data.pages.find((p) => p.id === target);
      setRules(page?.override ?? page?.effective ?? data.defaultRules);
    }
    setSlots(null);
  }, [data, target, brandId]);

  const previewPlatform: Platform = useMemo(() => {
    if (target) return data?.pages.find((p) => p.id === target)?.platform ?? "facebook";
    return data?.pages[0]?.platform ?? "facebook";
  }, [data, target]);

  function set<K extends keyof ScheduleRules>(key: K, value: ScheduleRules[K]) {
    setRules((r) => ({ ...r, [key]: value }));
    setSlots(null);
  }

  function toggleDay(day: number) {
    const next = rules.activeDays.includes(day)
      ? rules.activeDays.filter((d) => d !== day)
      : [...rules.activeDays, day].sort((a, b) => a - b);
    set("activeDays", next);
  }

  function setWindow(index: number, patch: Partial<TimeWindow>) {
    set(
      "timeWindows",
      rules.timeWindows.map((w, i) => (i === index ? { ...w, ...patch } : w)),
    );
  }

  function validate(): string | null {
    const result = scheduleRulesSchema.safeParse(rules);
    if (result.success) return null;
    // Show the first problem rather than a wall — they are usually related.
    return result.error.issues[0]?.message ?? "Those settings are not valid";
  }

  async function onPreview() {
    const invalid = validate();
    setError(invalid);
    if (invalid) return;
    try {
      setSlots(await preview.mutateAsync({ rules, platform: previewPlatform, days: 14 }));
    } catch (err) {
      setError(apiErrorMessage(err, "Could not build a preview"));
    }
  }

  async function onSave() {
    const invalid = validate();
    setError(invalid);
    if (invalid) return;
    try {
      await save.mutateAsync({ socialAccountId: target, rules });
      setSavedAt(new Date());
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save"));
    }
  }

  if (brands.isLoading) return <p className="text-sm text-muted">{t("errors.loading")}</p>;
  if (!brands.data?.length) {
    return <p className="text-sm text-muted">{t("schedule.noBrands")}</p>;
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-ink">
            <CalendarClock size={18} className="text-primary-600" />
            {t("schedule.title")}
          </h1>
          <p className="text-sm text-muted">
            {t("schedule.subtitle", { timezone: data?.brand.timezone ?? "" })}
          </p>
        </div>

        <select
          value={brandId ?? ""}
          onChange={(e) => {
            setBrandId(e.target.value);
            setTarget(null);
          }}
          className="select"
        >
          {brands.data.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </header>

      {/* Which rule set is being edited. */}
      <div className="flex flex-wrap gap-2">
        <TargetChip active={target === null} onClick={() => setTarget(null)}>
          {t("schedule.brandDefault")}
        </TargetChip>
        {data?.pages.map((page) => (
          <TargetChip key={page.id} active={target === page.id} onClick={() => setTarget(page.id)}>
            {page.displayName}
            <span className="ml-1 text-xs opacity-70">
              {page.source === "page" ? t("schedule.custom") : t("schedule.inherited")}
            </span>
          </TargetChip>
        ))}
      </div>

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <section className="card-pad">
        <h2 className="mb-3 text-sm font-semibold">{t("schedule.presetsTitle")}</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {(data?.presets ?? SCHEDULE_PRESETS).map((preset) => (
            <button
              key={preset.key}
              onClick={() => {
                setRules(preset.rules);
                setSlots(null);
              }}
              className="rounded-lg border border-border p-3 text-left text-sm hover:border-primary-400 hover:bg-primary-50"
            >
              <span className="font-medium">{t(preset.labelKey)}</span>
              <span className="mt-0.5 block text-xs text-muted">{t(preset.descriptionKey)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-border bg-white p-4">
        <div>
          <h2 className="mb-2 text-sm font-semibold">{t("schedule.daysTitle")}</h2>
          <div className="flex flex-wrap gap-2">
            {DAY_KEYS.map((key, day) => (
              <button
                key={key}
                onClick={() => toggleDay(day)}
                aria-pressed={rules.activeDays.includes(day)}
                className={`rounded-lg border px-3 py-1.5 text-sm ${
                  rules.activeDays.includes(day)
                    ? "border-primary-500 bg-primary-50 text-primary-800"
                    : "border-border text-muted"
                }`}
              >
                {t(`schedule.days.${key}`)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h2 className="mb-2 text-sm font-semibold">{t("schedule.windowsTitle")}</h2>
          <div className="space-y-2">
            {rules.timeWindows.map((window, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="time"
                  value={window.start}
                  onChange={(e) => setWindow(i, { start: e.target.value })}
                  className="select min-w-0 px-2.5 py-1.5"
                />
                <span className="shrink-0 text-muted">–</span>
                <input
                  type="time"
                  value={window.end}
                  onChange={(e) => setWindow(i, { end: e.target.value })}
                  className="select min-w-0 px-2.5 py-1.5"
                />
                {rules.timeWindows.length > 1 ? (
                  <button
                    onClick={() =>
                      set(
                        "timeWindows",
                        rules.timeWindows.filter((_, j) => j !== i),
                      )
                    }
                    className="shrink-0 p-1.5 text-muted hover:text-red-600"
                    aria-label={t("schedule.removeWindow")}
                  >
                    <Trash2 size={15} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <button
            onClick={() =>
              set("timeWindows", [...rules.timeWindows, { start: "12:00", end: "14:00" }])
            }
            className="mt-2 inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
          >
            <Plus size={14} /> {t("schedule.addWindow")}
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <NumberField
            label={t("schedule.postsPerWeekMin")}
            value={rules.postsPerWeekMin}
            min={0}
            max={50}
            onChange={(v) => set("postsPerWeekMin", v)}
          />
          <NumberField
            label={t("schedule.postsPerWeekMax")}
            value={rules.postsPerWeekMax}
            min={1}
            max={50}
            onChange={(v) => set("postsPerWeekMax", v)}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={rules.useBestTimes}
            onChange={(e) => set("useBestTimes", e.target.checked)}
          />
          {t("schedule.useBestTimes")}
        </label>
      </section>

      <section className="card-pad">
        <button
          onClick={() => setAdvanced((a) => !a)}
          className="text-sm font-semibold text-primary-700 hover:underline"
        >
          {advanced ? t("schedule.hideAdvanced") : t("schedule.showAdvanced")}
        </button>

        {advanced ? (
          <div className="mt-3 space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <NumberField
                label={t("schedule.jitterMinutes")}
                value={rules.jitterMinutes}
                min={0}
                max={120}
                onChange={(v) => set("jitterMinutes", v)}
              />
              <NumberField
                label={t("schedule.minGapHours")}
                value={rules.minGapHours}
                min={0}
                max={72}
                onChange={(v) => set("minGapHours", v)}
              />
              <NumberField
                label={t("schedule.staggerMinutes")}
                value={rules.staggerPlatformsMinutes}
                min={0}
                max={180}
                onChange={(v) => set("staggerPlatformsMinutes", v)}
              />
            </div>

            <div>
              <span className="mb-1 block text-sm font-medium">{t("schedule.quietHours")}</span>
              {rules.quietHours ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={rules.quietHours.start}
                    onChange={(e) =>
                      set("quietHours", { ...rules.quietHours!, start: e.target.value })
                    }
                    className="select min-w-0 px-2.5 py-1.5"
                  />
                  <span className="shrink-0 text-muted">–</span>
                  <input
                    type="time"
                    value={rules.quietHours.end}
                    onChange={(e) =>
                      set("quietHours", { ...rules.quietHours!, end: e.target.value })
                    }
                    className="select px-2.5 py-1.5"
                  />
                  <button
                    onClick={() => set("quietHours", null)}
                    className="text-muted hover:text-red-600"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => set("quietHours", { start: "22:00", end: "06:00" })}
                  className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
                >
                  <Plus size={14} /> {t("schedule.addQuietHours")}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => void onPreview()}
          disabled={preview.isPending}
          className="btn-secondary btn-sm"
        >
          <Eye size={15} /> {t("schedule.preview")}
        </button>
        <button
          onClick={() => void onSave()}
          disabled={save.isPending}
          className="btn-primary btn-sm"
        >
          <Save size={15} /> {t("schedule.save")}
        </button>

        {target !== null && data?.pages.find((p) => p.id === target)?.source === "page" ? (
          <button
            onClick={() => void clearOverride.mutateAsync(target)}
            className="btn-ghost btn-sm"
          >
            <RotateCcw size={15} /> {t("schedule.useBrandDefault")}
          </button>
        ) : null}

        {savedAt ? (
          <span className="text-sm text-green-700">
            {t("schedule.savedAt", { time: savedAt.toLocaleTimeString() })}
          </span>
        ) : null}
      </div>

      {slots ? (
        <section className="card-pad">
          <h2 className="mb-2 text-sm font-semibold">
            {t("schedule.previewTitle", { count: slots.length })}
          </h2>
          {slots.length === 0 ? (
            <p className="text-sm text-muted">{t("schedule.previewEmpty")}</p>
          ) : (
            <ul className="grid gap-1 sm:grid-cols-2">
              {slots.map((slot) => (
                <li key={slot.at} className="flex justify-between rounded border border-border px-3 py-1.5 text-sm">
                  <span>{slot.day}</span>
                  <span className="font-mono">{slot.localTime}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-muted">{t("schedule.previewNote")}</p>
        </section>
      ) : null}
    </div>
  );
}

function TargetChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm ${
        active ? "border-primary-500 bg-primary-50 text-primary-800" : "border-border text-muted"
      }`}
    >
      {children}
    </button>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="input"
      />
    </label>
  );
}
